import React, { ComponentType, FC } from 'react';
import { createContext } from 'react-broadcast';
import { Serializable } from '../types';
import {
  FeatureToggleService,
  FeatureToggleServiceImpl,
} from '../services/FeatureToggleService';
import { noop } from '../../utilities/noop';

export interface ToggleOptions {
  isLocallyPermanent?: boolean;
  isVariantToggle?: boolean;
}

export interface ToggleContext {
  contentfulTogglesInitialized: boolean;
  overridenTogglesCache: Record<string, Serializable | undefined>;
  updateToggleValue(
    toggleName: string,
    toggleValue: Serializable,
    options?: ToggleOptions,
  ): void;
  removeToggleValue(toggleName: string, options?: ToggleOptions);
  featureToggleService: FeatureToggleService;
}

export const defaultContext: ToggleContext = {
  contentfulTogglesInitialized: false,
  overridenTogglesCache: {},
  updateToggleValue: noop,
  removeToggleValue: noop,
  featureToggleService: FeatureToggleServiceImpl.getInstance(),
};

const { Provider, Consumer } = createContext(defaultContext);

export { Provider, Consumer };

export function withToggleContext<T extends ToggleContext>(
  Component: ComponentType<T>,
  displayName?: string,
): ComponentType<Partial<T>> {
  const WrappedComponent: FC<T> = (props) => (
    <Consumer>
      {(context: ToggleContext) => {
        return <Component {...context} {...props} />;
      }}
    </Consumer>
  );

  WrappedComponent.displayName =
    displayName || Component.displayName || 'withToggleContext(Unknown)';

  return WrappedComponent as ComponentType<Partial<T>>;
}
